import React from "react";
import { ArrowUpRight, Film, Handshake } from "lucide-react";
import { useContact } from "@/contexts/ContactContext";
import { Reveal } from "@/components/Reveal";

export const Showcase = () => {
  return (
    <section
      id="showcase"
      data-testid="showcase-section"
      className="relative bg-[#000000] py-16 md:py-24 lg:py-28 border-t border-white/5"
    >
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 md:px-12 lg:px-16">
        <Reveal className="text-center mb-12 md:mb-16">
          <div className="flex items-center justify-center gap-3 mb-5">
            <span className="h-px w-10 bg-fuchsia-500" />
            <span className="text-[10px] uppercase tracking-[0.32em] text-fuchsia-400 font-bold">
              Showcase · Our Two Flagships
            </span>
            <span className="h-px w-10 bg-fuchsia-500" />
          </div>
          <h2
            data-testid="showcase-headline"
            className="font-display text-3xl sm:text-4xl lg:text-5xl xl:text-[64px] font-black tracking-[-0.03em] text-white leading-[0.98] max-w-5xl mx-auto"
          >
            The work we obsess over.
          </h2>
        </Reveal>

        <ShowcaseBlock
          id="showcase-video"
          testid="showcase-video"
          eyebrow="Video Production"
          icon={Film}
          title="Cinematic content, built for attention."
          body="We deliver high-impact visual content for events, brands, corporates, and products — capturing attention and creating lasting impact. Our craft spans brand films, event coverage, social reels, and fast-turnaround deliverables, end-to-end."
          tags={[
            "Brand Videos",
            "Event Coverage",
            "Social Reels",
            "Corporate Films",
          ]}
          image="https://images.unsplash.com/photo-1492691527719-9d1e07e534b4?crop=entropy&cs=srgb&fm=jpg&q=85&w=1800"
          reverse={false}
          service="Video Production & Creative"
        />

        <ShowcaseBlock
          id="showcase-partnerships"
          testid="showcase-partnerships"
          eyebrow="Partnerships & Sponsorships"
          icon={Handshake}
          title="Visibility, multiplied through the right rooms."
          body="We architect partnerships and sponsorships that put your brand in front of the right audiences — from influencer collaborations to flagship event deals. Sourcing, negotiation, activation and reporting, owned end-to-end."
          tags={[
            "Influencer Deals",
            "Event Sponsorships",
            "Co-Branding",
            "Activation & Reporting",
          ]}
          image="https://images.unsplash.com/photo-1517457373958-b7bdd4587205?crop=entropy&cs=srgb&fm=jpg&q=85&w=1800"
          reverse={true}
          service="Partnerships & Sponsorships"
        />
      </div>
    </section>
  );
};

const ShowcaseBlock = ({
  id,
  testid,
  eyebrow,
  title,
  body,
  tags,
  image,
  reverse,
  icon: Icon,
  service,
}) => {
  const { openContact } = useContact();
  return (
    <div
      id={id}
      data-testid={testid}
      className="mt-14 md:mt-20 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center scroll-mt-28"
    >
      <Reveal
        variant={reverse ? "right" : "left"}
        className={`lg:col-span-7 relative ${reverse ? "lg:order-2" : ""}`}
      >
        <div className="relative aspect-[16/10] overflow-hidden border border-white/10">
          <img
            src={image}
            alt={title}
            className="w-full h-full object-cover hover:scale-105 transition-transform duration-[2000ms] ease-out"
          />
          <div className="absolute inset-0 bg-gradient-to-tr from-black/60 via-transparent to-fuchsia-500/15" />
          <div className="absolute top-4 left-4 sm:top-5 sm:left-5 inline-flex items-center gap-2 bg-black/60 backdrop-blur-md border border-white/10 px-3 py-1.5">
            <Icon className="w-3.5 h-3.5 text-fuchsia-400" />
            <span className="text-[10px] uppercase tracking-[0.3em] text-white/80 font-bold">
              {eyebrow}
            </span>
          </div>
        </div>
      </Reveal>

      <Reveal
        variant={reverse ? "left" : "right"}
        delay={100}
        className={`lg:col-span-5 ${reverse ? "lg:order-1" : ""}`}
      >
        <h3 className="font-display text-2xl md:text-3xl lg:text-4xl font-black tracking-[-0.03em] text-white leading-[1.1]">
          {title}
        </h3>
        <p className="mt-5 text-sm md:text-base text-white/60 leading-relaxed font-body">
          {body}
        </p>
        <ul className="mt-7 flex flex-wrap gap-2">
          {tags.map((t) => (
            <li
              key={t}
              className="text-[11px] uppercase tracking-[0.2em] border border-fuchsia-500/30 text-fuchsia-300 bg-fuchsia-500/[0.05] px-3 py-1.5 font-medium"
            >
              {t}
            </li>
          ))}
        </ul>
        <button
          type="button"
          onClick={() => openContact({ service, source: testid })}
          data-testid={`${testid}-cta`}
          className="mt-9 inline-flex w-full sm:w-auto items-center justify-center gap-2 border border-white/20 hover:border-fuchsia-400 hover:bg-white/5 text-white font-semibold px-6 py-3.5 rounded-full transition-all"
        >
          Brief us on a project <ArrowUpRight className="w-4 h-4" />
        </button>
      </Reveal>
    </div>
  );
};
